'use strict';

angular.module('licensure.services')
    .factory('Documents', ['$http', '$q', 'Upload', function ($http, $q, Upload) {
        return {
            getByID: function (id) {
                let deferredDocInfo = $q.defer();

                this.storageAvailable().then(storageResponse => {
                    if(storageResponse.documentStorageAvailable) {
                        $http.get(`${apiLocation}/api/Core/StorageDocument/${id}`)
                        .then(response => {
                            deferredDocInfo.resolve(response.data)
                        })
                        .catch((reason) => {
                            deferredDocInfo.reject(reason.data?.message ?? 'An unknown error occured retrieving document info');
                         });
                    } else {
                        $http.get(apiLocation + '/api/Documents/' + id)
                        .then(response => deferredDocInfo.resolve(response.data))
                        .catch(reason => deferredDocInfo.reject(reason));
                    }
                });

                return deferredDocInfo.promise
            },

            save: function (document, saveAll, value) {
                var d = $q.defer();

                var method = 'POST';
                var url = apiLocation + '/api/Documents/';
                if (!isUndefined(document.id)) {
                    method = 'PUT'
                    url += document.id
                }

                $http({
                    method: method,
                    url: url,
                    data: {
                        document: document,
                        saveAll: saveAll,
                        value: value
                    }
                })
                    .then(function (response) {
                        d.resolve(response.data);
                    })
                    .catch(function (response) {
                        // handle error
                    });

                return d.promise;
            },

            delete: function (id) {
                var d = $q.defer();

                $http.delete(apiLocation + '/api/Documents/' + id, {
                    params: {

                    }
                })
                    .then(function (response) {
                        d.resolve(response.data);
                    })
                    .catch(function (response) {
                        // handle error
                    });

                return d.promise;
            },

            upload: function (document, value) {
                var d = $q.defer();

                // Legacy base64 upload to the ColdFusion-era endpoint. Kept as a fallback for when blob
                // storage is unavailable. Mirrors the original behavior: resolves the document on success and
                // resolves null on failure (callers rely on this never rejecting).
                var legacyUpload = () => {
                    Upload.upload({
                        url: apiLocation + '/api/Documents/upload',
                        data: {
                            file: document.file,
                            name: document.name,
                            description: document.description,
                            document_type_id: document.document_type_id == null ? undefined : document.document_type_id,
                            candidate_id: document.candidateID,
                            expiration_date: document.expiration_date,
                            object_id: value,
                            fromComplaint: document.fromComplaint == null ? undefined : document.fromComplaint,
                            data_object_id: document.data_object_id == null ? undefined : document.data_object_id,
                        }
                    }).then(function (resp) {
                        d.resolve(resp.data);
                    }, function (resp) {
                        console.log('Error status: ', resp);
                        d.resolve(null);
                    }, function (evt) {
                        var progressPercentage = parseInt(100.0 * evt.loaded / evt.total);
                        console.log('progress: ' + progressPercentage + '% ' + evt.config.data.file.name);
                    });
                };

                // Prefer blob storage (chunked upload) when it is available and there is a file to upload,
                // otherwise fall back to the legacy endpoint. If the availability check itself fails, fall back
                // to the legacy path. A failed blob upload resolves null (matching the legacy contract) rather
                // than rejecting, so existing callers that branch on a null result keep working.
                this.storageAvailable().then(storageResponse => {
                    if (storageResponse != null && storageResponse.documentStorageAvailable && document.file) {
                        this.storageUpload(value, document, null)
                            .then(response => d.resolve(response != null ? response.data : null))
                            .catch(() => d.resolve(null));
                    } else {
                        legacyUpload();
                    }
                }, () => {
                    legacyUpload();
                });

                return d.promise;
            },

            logAccess: function (id) {
                var d = $q.defer();

                $http({
                    method: 'POST',
                    url: apiLocation + '/api/Documents/LogDocumentAccess',
                    data: {
                        document_id: id
                    }
                })
                    .then(function (response) {
                        d.resolve(response.data);
                    })
                    .catch(function (response) {
                        // handle error
                    });

                return d.promise;
            },

            downloadZip: function (documentIds) {
                var d = $q.defer();

                $http({
                    method: 'POST',
                    url: apiLocation + '/api/Core/Zip',
                    data: JSON.stringify({
                        documentIds: documentIds.toString()
                    })
                }).then(function (response) {
                    d.resolve(response.data);
                }).catch(function (response) {
                    d.resolve(null);
                });

                return d.promise;
            },

            downloadZipWithProgress: function (documentIds, progressCallback) {
                let deferred = $q.defer();
                const controller = new AbortController();

                $http.post(apiLocation + '/api/Core/ZipSize', {
                    documentIds: documentIds.toString()
                }).then(function (sizeResponse) {
                    let totalSize = sizeResponse.data.size || 0;

                    fetch(apiLocation + '/api/Core/ZipStream', {
                        method: 'POST',
                        signal: controller.signal,
                        headers: {
                            'Content-Type': 'application/json'
                        },
                        body: JSON.stringify({
                            documentIds: documentIds.toString()
                        })
                    }).then(function (response) {
                        if (!response.ok) {
                            throw new Error('HTTP error, status = ' + response.status);
                        }

                        let loaded = 0;

                        let reader = response.body.getReader();
                        let chunks = [];

                        let readChunk = function () {
                            return reader.read().then(function (result) {
                                if (result.done) {
                                    let blob = new Blob(chunks, { type: 'application/zip' });
                                    deferred.resolve({
                                        data: blob
                                    });
                                    return;
                                }

                                chunks.push(result.value);
                                loaded += result.value.length;

                                if (progressCallback) {
                                    var percentComplete = 0;
                                    if (totalSize > 0) {
                                        percentComplete = Math.round((loaded / totalSize) * 100);
                                    }
                                    progressCallback({
                                        loaded: loaded,
                                        total: totalSize,
                                        percent: percentComplete
                                    });
                                }

                                // Continue reading next chunk
                                return readChunk();
                            });
                        };

                        return readChunk();
                    }).catch(function (error) {
                        deferred.reject({
                            status: 0,
                            statusText: error.message || 'Network error'
                        });
                    });
                }).catch(function (error) {
                    deferred.reject({
                        status: error.status,
                        statusText: 'Failed to download zip file'
                    });
                });

                // Attach abort to the promise that is actually returned to the caller so the
                // download can be cancelled. Aborting the fetch closes the connection, which trips
                // the server's request-abort token and stops the stream instead of continuing in the background.
                deferred.promise.abort = function () {
                    controller.abort();
                    deferred.reject({
                        status: 0,
                        statusText: 'Download aborted'
                    });
                };

                return deferred.promise;
            },

            getByUserForQuestionID: function (parameters) {
                var d = $q.defer();

                $http.get(apiLocation + '/api/Core/DocumentsByUser/' + parameters.user_id + '/' + parameters.question_id, {
                    params: {

                    }
                })
                    .then(function (response) {
                        d.resolve(response.data);
                    })
                    .catch(function (response) {
                        // handle error
                    });

                return d.promise;
            },

            getDocumentsByInspection: function (inspection_id) {
                var d = $q.defer();

                $http.get(apiLocation + '/api/Core/DocumentsByInspection/' + inspection_id, {
                    params: {

                    }
                })
                    .then(function (response) {
                        d.resolve(response.data);
                    })
                    .catch(function (response) {
                        d.resolve(null);
                    });

                return d.promise;
            },

            createDocumentObject: function (documentID, value, data_object) {
                var d = $q.defer();

                $http.post(apiLocation + "/api/Documents/createDocumentObject", {
                    documentID: documentID,
                    value: value,
                    data_object: data_object
                })
                    .then(function (response) {
                        d.resolve(response.data);
                    })
                    .catch(function (response) {
                        // handle error
                        d.resolve(null);
                    });

                return d.promise;
            },

            storageAvailable: function() {
                const d = $q.defer();
                
                $http.get(apiLocation + "/api/Core/StorageDocument/Available")
                .then(response => {
                    d.resolve(response.data);
                })
                .catch(response => {
                    d.reject("Storage is currently unavailable.");
                });

                return d.promise;
            },

            storageUpload: function(objectId, saveDocument, onProgressChange) {
                const chunkSize = 2 * 1024 * 1024
                const document = saveDocument.file;

                function computeSHA256(byteArrayBuffer) {
                    return crypto.subtle.digest("SHA-256", byteArrayBuffer).then(hashBuffer => {
                        const hashArray = new Uint8Array(hashBuffer);
                            const hashHex = Array.from(hashArray).map(hashByte => hashByte.toString(16).padStart(2, '0')).join("");
                            return hashHex;
                    });
                }

                function convertToBase64(bytes) {
                    const ab = new Uint8Array(bytes);
                    let binary = '';
                    for (let i = 0; i < ab.byteLength; i++) {
                        binary += String.fromCharCode(ab[i]);
                    }
                    return btoa(binary);
                }

                function uploadChunk(uploadId, file, chunkIndex, totalChunks, onProgress) {
                    const start = chunkIndex * chunkSize;
                    const end = Math.min(start + chunkSize, file.size);
                    const blobBytes = file.slice(start, end);
                    
                    return blobBytes.arrayBuffer().then((documentPartBytes) => {
                        const base64Blob = convertToBase64(documentPartBytes);
                        return {"documentPartBytes": documentPartBytes, "docPartBase64": base64Blob};
                    })
                    .then(({documentPartBytes, docPartBase64}) => {
                        const blobHash = computeSHA256(documentPartBytes);
                        return blobHash.then((sha256Hash) => {

                            const chunkData = {
                                'uploadId': uploadId,
                                'chunkIndex': chunkIndex,
                                'chunkHash': sha256Hash,
                                'chunk': docPartBase64
                            }

                            return $http.post(apiLocation + `/api/Core/StorageDocument/DocumentPart/`, chunkData, {
                                headers: { 'Content-Type': 'application/json' },
                            });
                        }).then(() => {
                            if (onProgress) {
                                onProgress({
                                    chunkIndex: chunkIndex,
                                    totalChunks: totalChunks,
                                    percent: Math.round(((chunkIndex + 1) / totalChunks) * 100)
                                });
                            }
                        })
                    });
                }

                const totalParts = Math.ceil(document.size / chunkSize);
                let uploadId;

                return $http.post(apiLocation + '/api/Core/StorageDocument/StartDocument', {
                    fileName: document.name,
                    fileSize: document.size
                }).then(response =>  {
                    uploadId = response.data.uploadId;
                    let uploadPromiseChain = $q.when();

                    for (let partIndex = 0; partIndex < totalParts; partIndex++) {
                        uploadPromiseChain = uploadPromiseChain.then(() => { 
                            return uploadChunk(uploadId, document, partIndex, totalParts, onProgressChange);
                        })
                    };

                    return uploadPromiseChain;
                }).then((response) => {
                    return $http.post(apiLocation + '/api/Core/StorageDocument/FinishDocument', {
                        uploadId: uploadId,
                        totalParts: totalParts,
                        name: saveDocument.name,
                        description: saveDocument.description,
                        filename: document.name,
                        fileSize: document.size,
                        originalFilename: document.name,
                        documentTypeId: saveDocument.document_type_id == null ? undefined : saveDocument.document_type_id,
                        candidateId: saveDocument.candidateID,
                        expirationDate: saveDocument.expiration_date,
                        fromComplaint: saveDocument.fromComplaint == null ? undefined : saveDocument.fromComplaint,
                        dataObjectId: saveDocument.data_object_id == null ? undefined : saveDocument.data_object_id,
                        objectId: objectId
                    });
                });
            },

            updateDocument: function(documentID, document) {
                return $http.put(`${apiLocation}/api/Core/StorageDocument/${documentID}`, document)
                    .then(response => response.data);
            }
        }
    }])
;